import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import {
  buildMagicSingleFormUrl,
  getMagicBaseUrl,
  legacyFormSourceRegistry,
  useSyncLegacyDarkMode,
} from '@/shared/legacyMagicBridge';
import { legacyFormAgenda } from './legacyFormAgenda';

interface LegacyFormShellPageProps {
  layerId: string;
}

/**
 * Hostí jeden Magic formulář v iframu (Magic běží v phoenix-single režimu).
 *
 * URL iframu sestaví z agenda params: `formGlobalName` určuje Magic Form,
 * `formParams` se předají jako URL parametry tomu formu uvnitř Magicu.
 *
 * Po načtení iframu se jeho `contentWindow` registruje v `legacyFormSourceRegistry`
 * pod `layerId`. To umožní `MagicControlBridge` napárovat příchozí `form/result`
 * zprávu na správnou Phoenix agendu (a zavřít přesně tu, ne libovolnou top).
 *
 * Navigace UVNITŘ Magic formu probublává postMessage akcí `form/open`,
 * kterou v rootu app zpracovává `MagicControlBridge` a otevírá nové
 * instance této agendy (stack).
 */
export function LegacyFormShellPage({ layerId }: LegacyFormShellPageProps) {
  const { open } = useAgendaParams(legacyFormAgenda, layerId);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const iframeUrl = useMemo(
    () => buildMagicSingleFormUrl(open.formGlobalName, open.formParams),
    [open.formGlobalName, open.formParams]
  );

  // Runtime přepnutí dark/light propíše do tohoto form iframu (load-time stav nese URL param).
  useSyncLegacyDarkMode(() => iframeRef.current?.contentWindow ?? null);

  const handleLoad = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (win) legacyFormSourceRegistry.register(win, layerId);
  }, [layerId]);

  useEffect(() => {
    return () => {
      legacyFormSourceRegistry.unregister(layerId);
    };
  }, [layerId]);

  if (!getMagicBaseUrl()) {
    return (
      <div className="p-phx-4">
        <p className="text-phx-danger">
          Legacy form agenda vyžaduje vyplněný <code>LegacyMagic:BaseUrl</code> v backendovém
          appsettings. Endpoint <code>/api/config/runtime</code> vrátil prázdnou hodnotu.
        </p>
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      title={open.formGlobalName}
      src={iframeUrl}
      className="h-full w-full border-0"
      data-testid="legacy-form-iframe"
      data-layer-id={layerId}
      onLoad={handleLoad}
    />
  );
}
